import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { DatePicker } from './ui/DatePicker';
import { ArrowLeft, Save } from 'lucide-react';
interface PenaltyProfileFormProps {
  onBack: () => void;
  onSave: () => void;
}
export function PenaltyProfileForm({
  onBack,
  onSave
}: PenaltyProfileFormProps) {
  // Generate a random UUID for display
  const [profileId] = useState(`PEN-${Math.random().toString(36).substr(2, 9).toUpperCase()}`);
  // Form State
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'Flat Fee',
    amount: '',
    glAccount: '',
    effectiveDate: '',
    trigger: '',
    frequency: '',
    minCap: '',
    maxCap: ''
  });
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  // Helper text logic based on type
  const getAmountHelperText = () => {
    switch (formData.type) {
      case 'Flat Fee':
        return 'Fixed amount in PKR';
      case 'Percentage':
        return 'Percentage of overdue balance';
      case 'Tiered':
        return 'Base amount for tier calculation';
      default:
        return 'Amount or percentage charged';
    }
  };
  return <div className="flex flex-col h-full bg-gray-50" data-id="element-458">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-459">
        <div className="flex items-center" data-id="element-460">
          <Button variant="ghost" onClick={onBack} className="mr-4" data-id="element-461">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-462" />
            Back to List
          </Button>
          <div data-id="element-463">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-464">
              New Penalty Profile
            </h1>
            <p className="text-sm text-gray-500" data-id="element-465">
              Configure penalty rules and charges
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-466">
          <Button variant="secondary" onClick={onBack} data-id="element-467">
            Cancel
          </Button>
          <Button onClick={onSave} data-id="element-468">
            <Save className="h-4 w-4 mr-2" data-id="element-469" />
            Save Profile
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full space-y-8" data-id="element-470">
        {/* Section 1: Penalty Profile Header */}
        <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-471">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100" data-id="element-472">
            Penalty Profile Header
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-473">
            <div className="space-y-1.5" data-id="element-474">
              <label className="text-sm font-medium text-gray-700" data-id="element-475">
                Penalty ID
              </label>
              <div className="flex h-10 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500" data-id="element-476">
                {profileId}
              </div>
            </div>

            <Input label="Penalty Code" value={formData.code} onChange={e => handleChange('code', e.target.value)} placeholder="e.g., PEN-LATE-001" helperText="Used for integrations and product setup" required data-id="element-477" />

            <Input label="Penalty Name" value={formData.name} onChange={e => handleChange('name', e.target.value)} placeholder="e.g., Late Payment Charge" required data-id="element-478" />

            <Select label="Penalty Type" value={formData.type} onChange={e => handleChange('type', e.target.value)} options={[{
            value: 'Flat Fee',
            label: 'Flat Fee'
          }, {
            value: 'Percentage',
            label: 'Percentage'
          }, {
            value: 'Tiered',
            label: 'Tiered'
          }]} required data-id="element-479" />

            <Input label="Penalty Amount" type="number" value={formData.amount} onChange={e => handleChange('amount', e.target.value)} placeholder="e.g., 500 or 2.5" helperText={getAmountHelperText()} required data-id="element-480" />

            <Select label="GL Account" value={formData.glAccount} onChange={e => handleChange('glAccount', e.target.value)} options={[{
            value: '5100',
            label: '5100 - Penalty Income'
          }, {
            value: '5200',
            label: '5200 - Late Payment Charges'
          }, {
            value: '5300',
            label: '5300 - Default Charges'
          }]} required data-id="element-481" />

            <DatePicker label="Effective Date" value={formData.effectiveDate} onChange={e => handleChange('effectiveDate', e.target.value)} required data-id="element-482" />

            <div className="md:col-span-2" data-id="element-483">
              <label className="text-sm font-medium text-gray-700 mb-1.5 block" data-id="element-484">
                Description
              </label>
              <textarea className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50" placeholder="Describe the penalty, conditions under which it applies and its enforcement..." value={formData.description} onChange={e => handleChange('description', e.target.value)} data-id="element-485" />
            </div>
          </div>
        </section>

        {/* Section 2: Penalty Application Details */}
        <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-6 duration-500 delay-100" data-id="element-486">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100" data-id="element-487">
            Penalty Application Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-488">
            <Select label="Penalty Trigger" value={formData.trigger} onChange={e => handleChange('trigger', e.target.value)} options={[{
            value: 'Late Payment',
            label: 'Late Payment - Delayed beyond due date'
          }, {
            value: 'Missed Payment',
            label: 'Missed Payment - Not made at all'
          }, {
            value: 'Overdue Charge',
            label: 'Overdue Charge - Overdue for set period'
          }]} required data-id="element-489" />

            <Select label="Penalty Frequency" value={formData.frequency} onChange={e => handleChange('frequency', e.target.value)} options={[{
            value: 'Once',
            label: 'Once - Applied at time of event'
          }, {
            value: 'Recurring',
            label: 'Recurring - Applied repeatedly'
          }]} required data-id="element-490" />

            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100 mt-2" data-id="element-491">
              <div className="md:col-span-2" data-id="element-492">
                <h3 className="text-sm font-medium text-gray-900 mb-1" data-id="element-493">
                  Penalty Caps (Optional)
                </h3>
                <p className="text-xs text-gray-500 mb-4" data-id="element-494">
                  Regulatory and risk control limits
                </p>
              </div>

              <Input label="Minimum Cap" type="number" value={formData.minCap} onChange={e => handleChange('minCap', e.target.value)} placeholder="Optional minimum amount" data-id="element-495" />

              <Input label="Maximum Cap" type="number" value={formData.maxCap} onChange={e => handleChange('maxCap', e.target.value)} placeholder="Optional maximum amount" data-id="element-496" />
            </div>
          </div>
        </section>
      </div>
    </div>;
}